import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { transformApiDataToChartFormat } from "@/lib/tag-data-utils.js";
import { formatTimeAxis } from "@/lib/datetime-utils.js";
import {
  getTimeRangeConfig,
  computeEnalysisTrendRangeMonths,
} from "@/utils/time-range-utils.js";
import { getEnalysisTrendsData } from "@/api/enalysis-api.js";
import {
  ENALYSIS_TREND_LIST_SOURCE,
  ENALYSIS_TRENDS_DATA_RANGE_MONTHS,
} from "@/constants/enalysis-trends-constants.js";
import {
  filterEnalysisMeasurementsByTrendRange,
  getCommaSeparatedKeysForEnalysisTrendsData,
  mergeEnalysisTrendsDataIntoParameters,
} from "@/lib/enalysis-trends-tag-utils.js";

/**
 * Custom hook to fetch and cache trend data for selected parameters
 * @param {Set} selectedParameters - Set of selected parameter IDs
 * @param {Array} getSelectedParameterData - Memoized array of selected parameter objects
 * @param {Object} compressor - Compressor/asset object
 * @param {string} selectedTimeRange - Selected time range
 * @param {string} customStartDate - Custom start date (for Custom range)
 * @param {string} customEndDate - Custom end date (for Custom range)
 * @param {Map} trendDataCache - Cache for trend data (Map<paramId, Map<period, rawData>>)
 * @param {Map} sevenDayDataCache - Cache for 7-day data (fallback)
 * @param {Function} getParameterId - Function to get unique ID for a parameter
 * @param {Function} setTrendDataCache - Setter for trend data cache
 * @returns {Object} { trendData, isLoadingTrend, trendError, fetchTrendData }
 */
export function useTrendData(
  selectedParameters,
  getSelectedParameterData,
  compressor,
  selectedTimeRange,
  customStartDate,
  customEndDate,
  trendDataCache,
  sevenDayDataCache,
  getParameterId,
  setTrendDataCache
) {
  const [trendData, setTrendData] = useState([]);
  const [trendDataRangeKey, setTrendDataRangeKey] = useState(null);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);
  const [trendError, setTrendError] = useState(null);

  // Use ref to always access latest customStartDate without triggering refetch
  const customStartDateRef = useRef(customStartDate);
  useEffect(() => {
    customStartDateRef.current = customStartDate;
  }, [customStartDate]);

  const expectedTrendRangeKey = useMemo(() => {
    if (selectedTimeRange === "Custom") {
      return customStartDate && customEndDate
        ? `custom-${customStartDate}-${customEndDate}`
        : null;
    }
    return selectedTimeRange;
  }, [selectedTimeRange, customStartDate, customEndDate]);

  const expectedTrendRangeKeyRef = useRef(expectedTrendRangeKey);
  expectedTrendRangeKeyRef.current = expectedTrendRangeKey;

  const trendFetchRequestRef = useRef(0);

  const trendDataForDisplay = useMemo(() => {
    if (
      expectedTrendRangeKey == null
      || trendDataRangeKey !== expectedTrendRangeKey
    ) {
      return [];
    }
    return trendData;
  }, [trendData, trendDataRangeKey, expectedTrendRangeKey]);

  // Helper: Fetch data for a single parameter (with cache optimization)
  // cacheKey: unique key for caching (includes dates for custom ranges)
  // displayPeriod: period format for chart display (24h, 7d, 30d)
  const fetchSingleParameterData = useCallback(
    async (
      param,
      fetchFn,
      cacheKey,
      displayPeriod,
      customStartDate,
      customEndDate
    ) => {
      const paramId = getParameterId(param);

      const enalysisRaw = param.apiData?.enalysisTrendRaw;
      if (
        param.apiData?.source === ENALYSIS_TREND_LIST_SOURCE
        && enalysisRaw?.Measurements?.length
      ) {
        const filtered = filterEnalysisMeasurementsByTrendRange(
          enalysisRaw.Measurements,
          cacheKey,
          customStartDate,
          customEndDate
        );
        const shaped = { Measurements: filtered };
        return {
          param,
          data: transformApiDataToChartFormat(
            shaped,
            displayPeriod,
            formatTimeAxis
          ),
        };
      }

      if (!param.apiData?.assetTagId || !param.apiData?.assetId) {
        return null;
      }

      // Check trend cache first
      const paramCache = trendDataCache.get(paramId);
      if (paramCache && paramCache.has(cacheKey)) {
        const cachedData = paramCache.get(cacheKey);
        if (cachedData) {
          const transformedData = transformApiDataToChartFormat(
            cachedData,
            displayPeriod,
            formatTimeAxis
          );
          return {
            param,
            data: transformedData,
          };
        }
      }

      // Check 7-day cache for 7d period (fallback) - only for non-custom ranges
      if (cacheKey === "7d" && sevenDayDataCache.has(paramId)) {
        const cached = sevenDayDataCache.get(paramId);
        if (cached.rawData) {
          const transformedData = transformApiDataToChartFormat(
            cached.rawData,
            displayPeriod,
            formatTimeAxis
          );
          return {
            param,
            data: transformedData,
          };
        }
      }

      // Fetch from API and cache the result
      try {
        const apiResponse = await fetchFn(
          param.apiData.assetTagId,
          param.apiData.assetId
        );

        // Cache the fetched data
        setTrendDataCache((prevCache) => {
          const newCache = new Map(prevCache);
          if (!newCache.has(paramId)) {
            newCache.set(paramId, new Map());
          }
          const paramCache = new Map(newCache.get(paramId));
          paramCache.set(cacheKey, apiResponse);
          newCache.set(paramId, paramCache);
          return newCache;
        });

        const transformedData = transformApiDataToChartFormat(
          apiResponse,
          displayPeriod,
          formatTimeAxis
        );
        return {
          param,
          data: transformedData,
        };
      } catch (err) {
        console.error(`Error fetching data for ${param.parameter}:`, err);
        return null;
      }
    },
    [sevenDayDataCache, trendDataCache, getParameterId, setTrendDataCache]
  );

  // Shared function to fetch trend data for selected tags
  // Accepts optional override dates for explicit custom date range fetches
  const fetchTrendData = useCallback(
    async (overrideStartDate, overrideEndDate) => {
      if (selectedParameters.size === 0 || !compressor) {
        setTrendData([]);
        setTrendDataRangeKey(null);
        return;
      }

      // Use override dates if provided, otherwise use ref/state values
      const startDate = overrideStartDate ?? customStartDateRef.current;
      const endDate = overrideEndDate ?? customEndDate;

      // Don't fetch if Custom is selected but both dates are not set
      if (selectedTimeRange === "Custom" && (!startDate || !endDate)) {
        setTrendData([]);
        setTrendDataRangeKey(null);
        return;
      }

      const requestId = ++trendFetchRequestRef.current;
      setIsLoadingTrend(true);
      setTrendError(null);

      const completedRangeKey =
        selectedTimeRange === "Custom"
          ? `custom-${startDate}-${endDate}`
          : selectedTimeRange;

      try {
        const selectedParams = getSelectedParameterData;
        const { fn: fetchFn, period } = getTimeRangeConfig(
          selectedTimeRange,
          startDate,
          endDate
        );

        // For Custom range, create a unique cache key that includes the dates
        // This prevents returning cached data from a different custom date range
        const cacheKey =
          selectedTimeRange === "Custom"
            ? `custom-${startDate}-${endDate}`
            : period;

        let chartParams = selectedParams;
        if (
          selectedTimeRange === "Custom"
          && startDate
          && endDate
          && compressor?.enalysisCompressorNum != null
          && compressor?.enalysisCompanyId != null
        ) {
          const enalysisSelected = selectedParams.filter(
            (p) => p?.apiData?.source === ENALYSIS_TREND_LIST_SOURCE
          );
          const keysCsv =
            getCommaSeparatedKeysForEnalysisTrendsData(enalysisSelected);
          const months = computeEnalysisTrendRangeMonths(startDate, endDate);
          if (keysCsv && months > ENALYSIS_TRENDS_DATA_RANGE_MONTHS) {
            try {
              const trendsPayload = await getEnalysisTrendsData(
                compressor.enalysisCompressorNum,
                months,
                compressor.enalysisCompanyId,
                keysCsv
              );
              const merged = mergeEnalysisTrendsDataIntoParameters(
                enalysisSelected,
                trendsPayload
              );
              const byId = new Map(
                merged.map((p) => [getParameterId(p), p])
              );
              chartParams = selectedParams.map((p) => {
                const id = getParameterId(p);
                return byId.get(id) ?? p;
              });
            } catch (err) {
              console.error("Enalysis custom-range trend fetch failed:", err);
            }
          }
        }

        // Fetch data for all selected tags in parallel
        // Pass both cacheKey (for caching) and period (for chart formatting)
        const fetchPromises = chartParams.map((param) =>
          fetchSingleParameterData(
            param,
            fetchFn,
            cacheKey,
            period,
            startDate,
            endDate
          )
        );

        const results = await Promise.all(fetchPromises);
        if (completedRangeKey !== expectedTrendRangeKeyRef.current) {
          return;
        }
        setTrendData(results.filter(Boolean));
        setTrendDataRangeKey(completedRangeKey);
      } catch (err) {
        if (requestId !== trendFetchRequestRef.current) {
          return;
        }
        console.error("Error fetching trend data:", err);
        setTrendError("Failed to load trend data. Please try again.");
        setTrendData([]);
        setTrendDataRangeKey(null);
      } finally {
        if (requestId === trendFetchRequestRef.current) {
          setIsLoadingTrend(false);
        }
      }
    },
    [
      selectedParameters,
      compressor,
      getSelectedParameterData,
      selectedTimeRange,
      customEndDate,
      fetchSingleParameterData,
      getParameterId,
    ]
  );

  // Fetch trend data when dependencies change
  useEffect(() => {
    // Skip if no parameters selected
    if (selectedParameters.size === 0 || !compressor) {
      setTrendData([]);
      setTrendDataRangeKey(null);
      return;
    }

    // For Custom range, only fetch if dates are already set
    // This handles the case when user adds more tags after applying custom dates
    if (selectedTimeRange === "Custom") {
      if (customStartDateRef.current && customEndDate) {
        // Dates are set, fetch data for current/new tags
        fetchTrendData();
      } else {
        // Custom selected but dates not set yet - clear and wait for user to apply
        setTrendData([]);
        setTrendDataRangeKey(null);
      }
      return;
    }

    fetchTrendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTimeRange,
    selectedParameters.size,
    compressor,
    customStartDate,
    customEndDate,
  ]);

  return {
    trendData: trendDataForDisplay,
    isLoadingTrend,
    trendError,
    fetchTrendData,
  };
}
